import React, { useContext, useEffect, useState, useMemo } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTaskDrawer } from '../hooks/useTaskDrawer';
import { TaskDrawer } from '../components/TaskDrawer';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { usePageHeader } from '../components/Layout';
import { Ic } from '../design/icons';
import { SectionCard, KpiBigCard, AreaChart, Donut, Track, Avatar, StatusPill, PriChip } from '../design/primitives';
import { PRIORITIES as PRIORITIES_MAP, userDisplay } from '../design/tokens';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Düşük' },
  { value: 'medium', label: 'Orta' },
  { value: 'high', label: 'Yüksek' },
  { value: 'urgent', label: 'Acil' },
];

function formatGreeting() {
  const h = new Date().getHours();
  if (h < 6) return 'iyi geceler';
  if (h < 12) return 'günaydın';
  if (h < 18) return 'iyi günler';
  return 'iyi akşamlar';
}

function formatDateHeader() {
  const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  const d = new Date();
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
}

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const { isWorkspaceAdmin } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [, setLastUpdate] = useState(new Date());

  const { selectedTaskId, isOpen: showDrawer, drawerRef, openDrawer, closeDrawer: handleCloseDrawer } = useTaskDrawer();

  const [showFilters, setShowFilters] = useState(false);
  const [filterProject, setFilterProject] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [personalMode, setPersonalMode] = useState(false);

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [allowedUserIds, setAllowedUserIds] = useState(null); // null = no filter (show all)
  const [teamLoadExpanded, setTeamLoadExpanded] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskIdParam = params.get('openTask');
    if (taskIdParam) {
      openDrawer(taskIdParam);
      navigate('/', { replace: true });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handler = (event) => {
      if (event.data && event.data.type === 'NAVIGATE' && event.data.taskId) {
        openDrawer(event.data.taskId);
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handler);
      return () => navigator.serviceWorker.removeEventListener('message', handler);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchFilterData();
    fetchData();
    const interval = setInterval(() => fetchData(), 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [period, filterProject, filterPriority, filterAssignee, filterTag, customStartDate, customEndDate, personalMode]);

  // Build the set of user IDs visible in Takım Yükü based on the current user's role.
  // Admin / Super Admin / Owner see everyone; others see only their team members.
  useEffect(() => {
    if (!user) { setAllowedUserIds(null); return; }
    if (isWorkspaceAdmin) { setAllowedUserIds(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/teams`);
        const ids = new Set();
        (res.data || []).forEach(t => {
          (t.memberIds || []).forEach(id => ids.add(id));
          (t.leaderIds || []).forEach(id => ids.add(id));
          if (t.leaderId) ids.add(t.leaderId);
        });
        if (user?.id) ids.add(user.id);
        if (!cancelled) setAllowedUserIds(ids);
      } catch (err) {
        console.error('Team scope load failed:', err);
        if (!cancelled) setAllowedUserIds(new Set(user?.id ? [user.id] : []));
      }
    })();
    return () => { cancelled = true; };
  }, [user, isWorkspaceAdmin]);

  const fetchFilterData = async () => {
    try {
      const [projectsRes, usersRes, tagsRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/reports/available-users`),
        axios.get(`${API}/tags`),
      ]);
      setProjects([...projectsRes.data].sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })));
      setUsers([...usersRes.data].sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })));
      setTags([...tagsRes.data].sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })));
    } catch (err) {
      console.error('Filter data load failed:', err);
    }
  };

  const fetchData = async () => {
    if (period === 'custom' && (!customStartDate || !customEndDate)) return;
    try {
      setLoading(true);
      const params = { period };
      if (filterProject !== 'all') params.projectId = filterProject;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (filterAssignee !== 'all') params.assignedTo = filterAssignee;
      if (filterTag !== 'all') params.tagId = filterTag;
      if (personalMode && user?.id) params.currentlyAssignedTo = user.id;
      if (period === 'custom' && customStartDate) params.start_date = customStartDate.toISOString();
      if (period === 'custom' && customEndDate) params.end_date = new Date(customEndDate.getTime() + 86400000 - 1).toISOString();
      const res = await axios.get(`${API}/stats`, { params });
      setStats(res.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Stats load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterProject('all');
    setFilterPriority('all');
    setFilterAssignee('all');
    setFilterTag('all');
    setPeriod('all');
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const activeFilterCount = [filterProject !== 'all', filterPriority !== 'all', filterAssignee !== 'all', filterTag !== 'all'].filter(Boolean).length;

  const actions = (
    <>
      <button
        className={`ox-btn ${personalMode ? 'ox-btn-primary' : ''}`}
        onClick={() => {
          const next = !personalMode;
          if (next) {
            setShowFilters(false);
            clearFilters();
          }
          setPersonalMode(next);
        }}
      >
        {personalMode ? Ic.dashboard : Ic.user || Ic.users}
        <span>{personalMode ? 'Genel' : 'Bana Özel'}</span>
      </button>
      <button
        className={`ox-btn ${showFilters ? 'ox-btn-primary' : ''}`}
        onClick={() => setShowFilters(!showFilters)}
        disabled={personalMode}
        style={personalMode ? { opacity: 0.5, pointerEvents: 'none' } : {}}
      >
        {Ic.filter}
        <span>Filtre</span>
        {activeFilterCount > 0 && (
          <span className="ox-mono text-[10px] px-1.5 rounded" style={{ background: 'var(--accent)', color: '#fff' }}>{activeFilterCount}</span>
        )}
      </button>
      <button className="ox-btn" onClick={fetchData} title="Yenile">
        <span className={loading ? 'animate-spin' : ''}>{Ic.refresh}</span>
      </button>
    </>
  );

  usePageHeader([{ label: 'Dashboard' }], actions, [personalMode, showFilters, activeFilterCount, loading]);

  const teamLoad = useMemo(() => {
    const groups = {};
    const tally = (list, isCompleted) => {
      (list || []).forEach(t => {
        const name = t.assignedToName || t.assignedUser;
        if (!name || name === 'Atanmamış') return;
        const id = t.assignedTo || name;
        if (allowedUserIds && !allowedUserIds.has(id)) return;
        if (!groups[id]) groups[id] = { id, name, total: 0, completed: 0 };
        groups[id].total += 1;
        if (isCompleted) groups[id].completed += 1;
      });
    };
    tally(stats?.pending, false);
    tally(stats?.inProgress, false);
    tally(stats?.reviewTasks, false);
    tally(stats?.completed, true);
    return Object.values(groups).sort((a, b) => b.total - a.total);
  }, [stats, allowedUserIds]);

  const TEAM_LOAD_COLLAPSED = 6;
  const teamLoadVisible = teamLoadExpanded ? teamLoad : teamLoad.slice(0, TEAM_LOAD_COLLAPSED);
  const teamLoadHasMore = teamLoad.length > TEAM_LOAD_COLLAPSED;

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: 400 }}>
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-[12.5px] ox-text-muted">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const totalActive = (stats?.inProgressTasks || 0) + (stats?.inReviewTasks || 0) + (stats?.pendingTasks || 0);
  const overdueCount = [
    ...(stats?.pending || []),
    ...(stats?.inProgress || []),
    ...(stats?.reviewTasks || []),
  ].filter(t => t.dueDate && new Date(t.dueDate) < new Date()).length;

  const myTasks = [
    ...(stats?.pending || []),
    ...(stats?.inProgress || []),
    ...(stats?.reviewTasks || []),
  ]
    .filter(t => personalMode || (t.assignedToId && user?.id && t.assignedToId === user.id))
    .slice(0, 4);

  const trendValues = (stats?.completionTrend || []).map(item => {
    let label;
    if (item.date.length === 4) label = item.date;
    else if (item.date.length === 7) {
      const [year, month] = item.date.split('-');
      const mn = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
      label = `${mn[parseInt(month) - 1]} ${year.slice(2)}`;
    } else {
      const d = new Date(item.date);
      label = `${d.getDate()}/${d.getMonth() + 1}`;
    }
    return { d: label, v: item.completed };
  });

  const totalCompletedTrend = trendValues.reduce((s, x) => s + x.v, 0);
  const avgDaily = trendValues.length ? (totalCompletedTrend / trendValues.length).toFixed(1) : '0';
  const bestDay = trendValues.reduce((max, x) => (x.v > max.v ? x : max), { v: 0, d: '—' });

  const priorityDist = [
    { label: 'Acil', value: stats?.urgentPriority || 0, color: PRIORITIES_MAP.urgent.hex },
    { label: 'Yüksek', value: stats?.highPriority || 0, color: PRIORITIES_MAP.high.hex },
    { label: 'Orta', value: stats?.mediumPriority || 0, color: PRIORITIES_MAP.medium.hex },
    { label: 'Düşük', value: stats?.lowPriority || 0, color: PRIORITIES_MAP.low.hex },
  ];
  const priorityTotal = priorityDist.reduce((s, x) => s + x.value, 0);

  const kpis = [
    {
      label: 'Aktif Görevler',
      value: totalActive,
      sparkColor: 'var(--accent)',
      spark: trendValues.length ? trendValues.map(x => x.v) : [0, 0, 0, 0, 0, 0, 0, 0],
      sub: stats?.totalTasks ? `${stats.totalTasks} toplam görev` : '',
    },
    {
      label: 'Gecikmiş',
      value: overdueCount,
      sparkColor: 'var(--danger)',
      spark: [overdueCount, overdueCount, overdueCount, overdueCount],
      sub: overdueCount > 0 ? 'Dikkat gerekiyor' : 'Her şey yolunda',
    },
    {
      label: 'Tamamlanma Oranı',
      value: `%${stats?.completionRate || 0}`,
      sparkColor: 'var(--good)',
      spark: trendValues.length ? trendValues.map(x => x.v) : [0, 0, 0, 0],
      sub: stats?.completedTasks ? `${stats.completedTasks} tamamlandı` : '',
    },
    {
      label: 'İnceleme Sırası',
      value: stats?.inReviewTasks || 0,
      sparkColor: 'var(--review)',
      spark: [stats?.inReviewTasks || 0, stats?.inReviewTasks || 0, stats?.inReviewTasks || 0, stats?.inReviewTasks || 0],
      sub: stats?.inReviewTasks ? 'Onay bekliyor' : 'Sıra boş',
    },
  ];

  return (
    <div className="ox-page-lg space-y-5">
      {/* Greeting */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11.5px] ox-text-dim uppercase ox-mono" style={{ letterSpacing: '.05em' }}>{formatDateHeader()}</div>
          <h1 className="ox-h1 mt-0.5">
            Merhaba {user?.name?.split(' ')[0] || ''}, {formatGreeting()}.
          </h1>
          <div className="ox-page-sub mt-1">
            Bu dönem <b className="ox-text-body ox-tnum">{stats?.completedTasks || 0}</b> görev tamamlandı
            {overdueCount > 0 && <> · <b className="ox-text-body ox-tnum">{overdueCount}</b> gecikmiş görev dikkatini bekliyor.</>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!personalMode && (
            <DateRangeFilter
              period={period}
              onPeriodChange={setPeriod}
              startDate={customStartDate}
              endDate={customEndDate}
              onStartDateChange={setCustomStartDate}
              onEndDateChange={setCustomEndDate}
              showLabel={false}
            />
          )}
        </div>
      </div>

      {/* Filter panel */}
      {showFilters && !personalMode && (
        <div className="ox-surface rounded-md p-4">
          <div className="ox-grid-form-4">
            <div className="ox-field">
              <label>Proje</label>
              <select className="ox-select" value={filterProject} onChange={e => setFilterProject(e.target.value)}>
                <option value="all">Tüm Projeler</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="ox-field">
              <label>Öncelik</label>
              <select className="ox-select" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
                <option value="all">Tüm Öncelikler</option>
                {PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div className="ox-field">
              <label>Atanan Kişi</label>
              <select className="ox-select" value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
                <option value="all">Tüm Kullanıcılar</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div className="ox-field">
              <label>Etiket</label>
              <select className="ox-select" value={filterTag} onChange={e => setFilterTag(e.target.value)}>
                <option value="all">Tüm Etiketler</option>
                {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          {activeFilterCount > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <button className="ox-btn ox-btn-ghost" onClick={clearFilters}>{Ic.x}<span>Temizle</span></button>
              <span className="ox-mono text-[11px] ox-text-dim">{activeFilterCount} filtre aktif</span>
            </div>
          )}
        </div>
      )}

      {/* KPIs */}
      <div className="ox-grid-kpi-4">
        {kpis.map((k, i) => <KpiBigCard key={i} {...k} />)}
      </div>

      {/* My tasks strip */}
      {myTasks.length > 0 && (
        <SectionCard
          title="Sana atanan görevler"
          subtitle="Devam eden işler"
          right={<>
            <span className="ox-mono text-[11px] ox-text-muted">{myTasks.length}</span>
            <Link className="ox-btn ox-btn-ghost text-[12px]" to="/tasks">Tümünü gör {Ic.chev}</Link>
          </>}
        >
          <div className="ox-grid-kpi-4">
            {myTasks.map(t => (
              <div key={t.id}
                onClick={() => openDrawer(t.id)}
                className="ox-surface-muted rounded-md p-3 flex flex-col gap-2"
                style={{ border: '1px solid var(--border)', cursor: 'pointer' }}
              >
                <div className="flex items-center gap-1.5">
                  <span className="ox-mono text-[10.5px] ox-text-dim">#{String(t.id).slice(-5)}</span>
                  {t.projectName && <span className="ox-mono text-[10.5px] ox-text-muted truncate flex-1">{t.projectName}</span>}
                  <StatusPill s={t.status} />
                </div>
                <div className="text-[13px] font-medium leading-snug" style={{ textWrap: 'pretty' }}>{t.title}</div>
                <div className="flex items-center gap-2 text-[11px] ox-text-muted mt-auto">
                  <PriChip p={t.priority} />
                  {t.dueDate && (
                    <span className="ml-auto flex items-center gap-1 ox-mono">
                      {Ic.clock}
                      {new Date(t.dueDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Bottom row */}
      <div className="ox-grid-main-trio">
        <SectionCard title="Tamamlanma Trendi" subtitle={`${trendValues.length} nokta · dönemsel kapatılan görev`}>
          {trendValues.length ? (
            <>
              <AreaChart data={trendValues} height={160} color="var(--accent)" />
              <div className="flex items-center gap-5 mt-3 pt-3 flex-wrap" style={{ borderTop: '1px solid var(--border)' }}>
                <div>
                  <div className="text-[10.5px] ox-text-dim uppercase">Toplam</div>
                  <div className="ox-tnum text-[18px] font-semibold">{totalCompletedTrend}</div>
                </div>
                <div>
                  <div className="text-[10.5px] ox-text-dim uppercase">Ortalama</div>
                  <div className="ox-tnum text-[18px] font-semibold">{avgDaily}</div>
                </div>
                <div>
                  <div className="text-[10.5px] ox-text-dim uppercase">En iyi</div>
                  <div className="ox-tnum text-[18px] font-semibold">{bestDay.v} <span className="text-[11px] ox-text-dim font-normal">{bestDay.d}</span></div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-10 text-[12.5px] ox-text-dim">Veri yok</div>
          )}
        </SectionCard>

        <SectionCard title="Öncelik Dağılımı" subtitle={`${priorityTotal} görev`}>
          <div className="flex items-center gap-4">
            <Donut segments={priorityDist} size={120} thickness={14} centerLabel="Toplam" centerValue={priorityTotal} />
            <div className="flex-1 space-y-2">
              {priorityDist.map(s => (
                <div key={s.label} className="flex items-center gap-2 text-[12px]">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
                  <span className="flex-1 ox-text-muted">{s.label}</span>
                  <span className="ox-tnum font-medium">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Takım Yükü" subtitle="Toplam / tamamlanan görevler">
          {teamLoad.length === 0 ? (
            <div className="text-center py-8 text-[12.5px] ox-text-dim">Atanmış görev yok</div>
          ) : (
            <>
              <div className="space-y-2.5">
                {teamLoadVisible.map(t => {
                  const pct = t.total > 0 ? (t.completed / t.total) * 100 : 0;
                  const done = t.total > 0 && t.completed === t.total;
                  const u = userDisplay({ id: t.id, name: t.name });
                  return (
                    <div key={t.id} className="flex items-center gap-2.5">
                      <Avatar u={u} size={22} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between text-[12px]">
                          <span className="truncate">{t.name.split(' ')[0]}</span>
                          <span className="ox-mono text-[11px] ox-text-muted" title="Toplam / Tamamlanan">
                            <span className="ox-text-body">{t.total}</span>
                            <span className="ox-text-dim"> / </span>
                            <span style={{ color: 'var(--good)' }}>{t.completed}</span>
                          </span>
                        </div>
                        <Track value={pct} color={done ? 'var(--good)' : 'var(--accent)'} />
                      </div>
                    </div>
                  );
                })}
              </div>
              {teamLoadHasMore && (
                <div className="mt-3 pt-3 flex justify-center" style={{ borderTop: '1px solid var(--border)' }}>
                  <button
                    type="button"
                    className="ox-btn ox-btn-ghost text-[12px]"
                    onClick={() => setTeamLoadExpanded(v => !v)}
                  >
                    {teamLoadExpanded ? (
                      <>
                        <span>Daralt</span>
                        {Ic.chev}
                      </>
                    ) : (
                      <>
                        <span>Tümünü göster ({teamLoad.length})</span>
                        {Ic.chev}
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </SectionCard>
      </div>

      {/* Task Detail Drawer */}
      <TaskDrawer
        selectedTaskId={selectedTaskId}
        isOpen={showDrawer}
        drawerRef={drawerRef}
        onClose={handleCloseDrawer}
        onDataChanged={() => {
          const params = { period, nocache: Date.now() };
          if (filterProject !== 'all') params.projectId = filterProject;
          if (filterPriority !== 'all') params.priority = filterPriority;
          if (filterAssignee !== 'all') params.assignedTo = filterAssignee;
          if (filterTag !== 'all') params.tagId = filterTag;
          if (personalMode && user?.id) params.currentlyAssignedTo = user.id;
          axios.get(`${API}/stats`, { params })
            .then(res => { setStats(res.data); setLastUpdate(new Date()); })
            .catch(() => {});
        }}
      />
    </div>
  );
}
